import React, { useState, useEffect, useCallback } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import {
  Card,
  Table,
  Tag,
  Space,
  Button,
  Select,
  Input,
  DatePicker,
  Typography,
  Row,
  Col,
  Dropdown,
  Modal,
} from "antd";
import {
  PlusOutlined,
  EyeOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  SendOutlined,
  MoreOutlined,
  ReloadOutlined,
  SearchOutlined,
  ExclamationCircleOutlined,
} from "@ant-design/icons";
import { toast } from "react-toastify";
import moment from "moment";
import {
  getAllGRNs,
  submitGRN,
  approveGRN,
  rejectGRN,
} from "../../api/inventoryApi";
import PageTitle from "../page-header/PageHeader";
import checkTokenExp from "../../utils/checkTokenExp";

const { Text } = Typography;
const { Search } = Input;
const { RangePicker } = DatePicker;
const { confirm } = Modal;

const statusConfig = {
  draft: { color: "default", label: "Draft" },
  submitted: { color: "processing", label: "Submitted" },
  approved: { color: "success", label: "Approved" },
  rejected: { color: "error", label: "Rejected" },
};

const GRNList = () => {
  // ── Auth ───────────────────────────────────────────────────
  const isLogged = Boolean(localStorage.getItem("isLogged"));
  const accessToken = localStorage.getItem("access-token");

  // ── State ──────────────────────────────────────────────────
  const navigate = useNavigate();
  const [grns, setGrns] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 20,
    total: 0,
  });
  const [filters, setFilters] = useState({
    status: undefined,
    search: "",
    qc_status: undefined,
    start_date: undefined,
    end_date: undefined,
  });

  // ── Fetch ──────────────────────────────────────────────────
  const fetchGRNs = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        page: pagination.current,
        count: pagination.pageSize,
      };
      if (filters.status) params.status = filters.status;
      if (filters.search) params.search = filters.search;
      if (filters.qc_status) params.qc_status = filters.qc_status;
      if (filters.start_date) params.start_date = filters.start_date;
      if (filters.end_date) params.end_date = filters.end_date;

      const result = await getAllGRNs(params);
      setGrns(result.data || []);
      setPagination((prev) => ({
        ...prev,
        total: result.total || result.pagination?.total || 0,
      }));
    } catch (error) {
      console.log(error.message);
      toast.error("Failed to load GRNs");
    } finally {
      setLoading(false);
    }
  }, [pagination.current, pagination.pageSize, filters]);

  useEffect(() => {
    fetchGRNs();
  }, [fetchGRNs]);

  // ── Auth guard (after hooks) ───────────────────────────────
  if (!isLogged) {
    return <Navigate to={"/auth/login"} replace={true} />;
  }
  checkTokenExp(accessToken);

  // ── Actions ────────────────────────────────────────────────
  const handleSubmit = (id, grnNumber) => {
    confirm({
      title: `Submit GRN ${grnNumber}?`,
      icon: <ExclamationCircleOutlined />,
      content: "This will send the GRN for approval.",
      onOk: async () => {
        try {
          await submitGRN(id);
          toast.success("GRN submitted for approval");
          fetchGRNs();
        } catch (error) {
          toast.error(error.response?.data?.message || "Failed to submit GRN");
        }
      },
    });
  };

  const handleApprove = (id, grnNumber) => {
    confirm({
      title: `Approve GRN ${grnNumber}?`,
      icon: <CheckCircleOutlined style={{ color: "#52c41a" }} />,
      content:
        "This will update the Purchase Order quantities and mark stock as received.",
      onOk: async () => {
        try {
          await approveGRN(id);
          toast.success("GRN approved — PO quantities updated");
          fetchGRNs();
        } catch (error) {
          toast.error(
            error.response?.data?.message || "Failed to approve GRN"
          );
        }
      },
    });
  };

  const handleReject = (id, grnNumber) => {
    let reason = "";
    confirm({
      title: `Reject GRN ${grnNumber}?`,
      icon: <CloseCircleOutlined style={{ color: "#ff4d4f" }} />,
      content: (
        <Input.TextArea
          placeholder="Reason for rejection..."
          rows={3}
          onChange={(e) => {
            reason = e.target.value;
          }}
        />
      ),
      okType: "danger",
      okText: "Reject",
      onOk: async () => {
        try {
          await rejectGRN(id, reason);
          toast.success("GRN rejected");
          fetchGRNs();
        } catch (error) {
          toast.error(
            error.response?.data?.message || "Failed to reject GRN"
          );
        }
      },
    });
  };

  // ── Action menu per row ────────────────────────────────────
  const getActionMenu = (record) => {
    const items = [
      {
        key: "view",
        label: "View Details",
        icon: <EyeOutlined />,
        onClick: () => navigate(`/inventory/grn/${record.id}`),
      },
    ];

    if (record.status === "draft") {
      items.push({
        key: "submit",
        label: "Submit for Approval",
        icon: <SendOutlined style={{ color: "#1890ff" }} />,
        onClick: () => handleSubmit(record.id, record.grn_number),
      });
    }

    if (record.status === "submitted") {
      items.push(
        {
          key: "approve",
          label: "Approve",
          icon: <CheckCircleOutlined style={{ color: "#52c41a" }} />,
          onClick: () => handleApprove(record.id, record.grn_number),
        },
        {
          key: "reject",
          label: "Reject",
          icon: <CloseCircleOutlined style={{ color: "#ff4d4f" }} />,
          onClick: () => handleReject(record.id, record.grn_number),
        }
      );
    }

    return { items };
  };

  // ── Columns ────────────────────────────────────────────────
  const columns = [
    {
      title: "GRN #",
      dataIndex: "grn_number",
      key: "grn_number",
      fixed: "left",
      width: 170,
      render: (text, record) => (
        <Button
          type="link"
          size="small"
          style={{ padding: 0 }}
          onClick={() => navigate(`/inventory/grn/${record.id}`)}
        >
          <Text strong>{text}</Text>
        </Button>
      ),
    },
    {
      title: "PO #",
      key: "po",
      width: 150,
      render: (_, r) =>
        r.purchase_order?.po_number || r.purchase_order_id || (
          <Text type="secondary">-</Text>
        ),
    },
    {
      title: "Supplier",
      key: "supplier",
      width: 160,
      ellipsis: true,
      render: (_, r) =>
        r.supplier?.name || r.supplier_id || (
          <Text type="secondary">-</Text>
        ),
    },
    {
      title: "Total Qty",
      dataIndex: "total_quantity",
      key: "total_qty",
      width: 100,
      render: (v) => v?.toLocaleString() || 0,
    },
    {
      title: "Accepted",
      dataIndex: "accepted_quantity",
      key: "accepted",
      width: 100,
      render: (v) => (
        <Text style={{ color: "#52c41a" }}>{v?.toLocaleString() || 0}</Text>
      ),
    },
    {
      title: "Rejected",
      dataIndex: "rejected_quantity",
      key: "rejected",
      width: 100,
      render: (v) =>
        v > 0 ? (
          <Text type="danger">{v.toLocaleString()}</Text>
        ) : (
          <Text type="secondary">0</Text>
        ),
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      width: 120,
      render: (s) => {
        const cfg = statusConfig[s] || { color: "default", label: s };
        return <Tag color={cfg.color}>{cfg.label}</Tag>;
      },
    },
    {
      title: "Date",
      key: "date",
      width: 110,
      render: (_, r) =>
        r.created_at ? moment(r.created_at).format("DD MMM YYYY") : "-",
    },
    {
      title: "",
      key: "actions",
      width: 60,
      fixed: "right",
      render: (_, record) => (
        <Dropdown menu={getActionMenu(record)} trigger={["click"]}>
          <Button type="text" icon={<MoreOutlined />} />
        </Dropdown>
      ),
    },
  ];

  return (
    <>
      <PageTitle title="Back" subtitle="Goods Receipt Notes" />

      <Card
        title="Goods Receipt Notes (GRN)"
        extra={
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => navigate("/inventory/grn/new")}
          >
            Create GRN
          </Button>
        }
      >
        {/* Filters */}
        <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
          <Col xs={24} sm={12} md={6}>
            <Search
              placeholder="Search GRN #..."
              allowClear
              onSearch={(value) =>
                setFilters((f) => ({ ...f, search: value }))
              }
              prefix={<SearchOutlined />}
            />
          </Col>
          <Col xs={24} sm={12} md={4}>
            <Select
              placeholder="Status"
              allowClear
              style={{ width: "100%" }}
              value={filters.status}
              onChange={(v) => setFilters((f) => ({ ...f, status: v }))}
            >
              <Select.Option value="draft">Draft</Select.Option>
              <Select.Option value="submitted">Submitted</Select.Option>
              <Select.Option value="approved">Approved</Select.Option>
              <Select.Option value="rejected">Rejected</Select.Option>
            </Select>
          </Col>
          <Col xs={24} sm={12} md={4}>
            <Select
              placeholder="QC Status"
              allowClear
              style={{ width: "100%" }}
              value={filters.qc_status}
              onChange={(v) => setFilters((f) => ({ ...f, qc_status: v }))}
            >
              <Select.Option value="pending">Pending</Select.Option>
              <Select.Option value="partial">Partial</Select.Option>
              <Select.Option value="passed">Passed</Select.Option>
              <Select.Option value="failed">Failed</Select.Option>
            </Select>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <RangePicker
              style={{ width: "100%" }}
              onChange={(dates) => {
                if (dates && dates[0] && dates[1]) {
                  setFilters((f) => ({
                    ...f,
                    start_date: dates[0].toISOString(),
                    end_date: dates[1].toISOString(),
                  }));
                } else {
                  setFilters((f) => ({
                    ...f,
                    start_date: undefined,
                    end_date: undefined,
                  }));
                }
              }}
            />
          </Col>
          <Col xs={24} sm={12} md={3}>
            <Button
              onClick={() =>
                setFilters({
                  status: undefined,
                  search: "",
                  qc_status: undefined,
                  start_date: undefined,
                  end_date: undefined,
                })
              }
            >
              Clear
            </Button>
          </Col>
        </Row>

        {/* Table */}
        <Table
          columns={columns}
          dataSource={grns}
          rowKey="id"
          loading={loading}
          pagination={{
            ...pagination,
            showSizeChanger: true,
            showTotal: (total) => `Total ${total} GRNs`,
            onChange: (page, pageSize) =>
              setPagination((prev) => ({ ...prev, current: page, pageSize })),
          }}
          scroll={{ x: 1200 }}
          size="middle"
        />
      </Card>
    </>
  );
};

export default GRNList;
